<template>
  <q-page class="q-pa-md bg-grey-2">
    <!-- Header Principal -->
    <q-card class="q-mb-md shadow-2 rounded-borders overflow-hidden">
      <div class="bg-primary text-white q-pa-md row items-center justify-between">
        <div class="row items-center">
          <q-icon name="gavel" size="md" class="q-mr-sm" />
          <div>
            <div class="text-h6 text-weight-bold">Sujetos Sin Capacidad Operativa (SSCO) - SUNAT</div>
            <div class="text-caption text-blue-2">
              Padrón oficial, scraping en vivo, importación masiva y auditoría API
            </div>
          </div>
        </div>
        <div class="row q-gutter-sm">
          <q-btn
            color="white"
            text-color="primary"
            icon="analytics"
            label="Top 10 Usuarios"
            unelevated
            rounded
            class="text-weight-bold"
            @click="abrirModalTopUsuarios"
          />
        </div>
      </div>
    </q-card>

    <!-- Tarjetas de Estadísticas (KPIs) -->
    <div class="row q-col-gutter-md q-mb-md">
      <div class="col-12 col-sm-6 col-md-4">
        <q-card class="shadow-2 rounded-borders bg-white">
          <q-card-section class="row items-center justify-between">
            <div>
              <div class="text-caption text-grey-7 text-uppercase text-weight-bold">
                Total SSCO en Padrón
              </div>
              <div class="text-h4 text-weight-bolder text-primary q-mt-xs">
                {{ kpis.total_ssco | formatNumber }}
              </div>
            </div>
            <q-avatar color="blue-1" text-color="primary" icon="dataset" size="lg" />
          </q-card-section>
        </q-card>
      </div>

      <div class="col-12 col-sm-6 col-md-4">
        <q-card class="shadow-2 rounded-borders bg-white">
          <q-card-section class="row items-center justify-between">
            <div>
              <div class="text-caption text-grey-7 text-uppercase text-weight-bold">
                Consultas API Realizadas
              </div>
              <div class="text-h4 text-weight-bolder text-indigo q-mt-xs">
                {{ kpis.total_consultas | formatNumber }}
              </div>
            </div>
            <q-avatar color="indigo-1" text-color="indigo" icon="manage_search" size="lg" />
          </q-card-section>
        </q-card>
      </div>

      <div class="col-12 col-sm-12 col-md-4">
        <q-card class="shadow-2 rounded-borders bg-white">
          <q-card-section class="row items-center justify-between">
            <div>
              <div class="text-caption text-grey-7 text-uppercase text-weight-bold">
                Detecciones Positivas SSCO
              </div>
              <div class="text-h4 text-weight-bolder text-negative q-mt-xs">
                {{ kpis.detecciones_positivas | formatNumber }}
              </div>
            </div>
            <q-avatar color="red-1" text-color="negative" icon="gavel" size="lg" />
          </q-card-section>
        </q-card>
      </div>
    </div>

    <!-- Barra de Acciones y Filtro -->
    <q-card class="q-mb-md shadow-2 rounded-borders">
      <q-card-section class="q-pa-md">
        <div class="row q-col-gutter-md items-center justify-between">
          <div class="col-12 col-md-5">
            <q-input
              v-model="busqueda"
              outlined
              dense
              placeholder="Buscar por RUC o Razón Social..."
              clearable
              @keyup.enter="buscar"
              @clear="buscar"
            >
              <template v-slot:prepend>
                <q-icon name="search" />
              </template>
              <template v-slot:append>
                <q-btn flat round dense icon="arrow_forward" @click="buscar" />
              </template>
            </q-input>
          </div>

          <div class="col-12 col-md-7 row justify-end q-gutter-sm">
            <q-btn
              color="secondary"
              icon="sync"
              label="Sincronizar con SUNAT"
              unelevated
              :loading="cargandoSunat"
              @click="sincronizarSunat"
            >
              <template v-slot:loading>
                <q-spinner class="on-left" />
                Sincronizando...
              </template>
            </q-btn>

            <q-btn
              color="teal"
              icon="description"
              label="Importar Excel"
              unelevated
              @click="dialogExcel = true"
            />

            <q-btn
              color="primary"
              icon="add"
              label="Nuevo Registro"
              unelevated
              @click="abrirCrear"
            />
          </div>
        </div>
      </q-card-section>
    </q-card>

    <!-- Tabla Principal Quasar -->
    <q-card class="shadow-2 rounded-borders">
      <q-table
        :data="listaSSCO"
        :columns="columns"
        row-key="id"
        :loading="loading"
        :pagination.sync="pagination"
        @request="onRequest"
        flat
        bordered
        no-data-label="No se encontraron registros en el padrón SSCO"
      >
        <!-- Columna RUC -->
        <template v-slot:body-cell-ruc="props">
          <q-td :props="props">
            <q-chip color="primary" text-color="white" dense class="text-weight-bold">
              {{ props.row.ruc }}
            </q-chip>
          </q-td>
        </template>

        <!-- Columna Razón Social y Domicilio -->
        <template v-slot:body-cell-razon_social="props">
          <q-td :props="props">
            <div class="text-weight-bold text-uppercase">{{ props.row.razon_social }}</div>
            <div class="text-caption text-grey-7 text-truncate" style="max-width: 320px;">
              {{ props.row.domicilio_fiscal || 'Sin domicilio fiscal registrado' }}
            </div>
          </q-td>
        </template>

        <!-- Columna Fechas Clave -->
        <template v-slot:body-cell-fechas="props">
          <q-td :props="props" class="text-caption">
            <div><strong>Emisión:</strong> {{ props.row.fecha_emision || '-' }}</div>
            <div><strong>Firme:</strong> {{ props.row.fecha_firme || '-' }}</div>
            <div><strong>Publicación:</strong> {{ props.row.fecha_publicacion || '-' }}</div>
          </q-td>
        </template>

        <!-- Columna Representante Legal -->
        <template v-slot:body-cell-representante="props">
          <q-td :props="props">
            <div class="text-weight-bold">{{ props.row.rep_nombre || 'No especificado' }}</div>
            <div class="text-caption text-grey-7" v-if="props.row.rep_documento">
              Doc: {{ props.row.rep_documento }}
            </div>
          </q-td>
        </template>

        <!-- Columna Acciones -->
        <template v-slot:body-cell-actions="props">
          <q-td :props="props" class="text-right">
            <q-btn
              flat
              round
              dense
              color="primary"
              icon="edit"
              @click="abrirEditar(props.row)"
            >
              <q-tooltip>Editar Registro</q-tooltip>
            </q-btn>
            <q-btn
              flat
              round
              dense
              color="negative"
              icon="delete"
              @click="confirmarEliminar(props.row)"
            >
              <q-tooltip>Eliminar Registro</q-tooltip>
            </q-btn>
          </q-td>
        </template>
      </q-table>
    </q-card>

    <!-- Modal Formulario (Crear / Editar) -->
    <q-dialog v-model="dialogForm" persistent>
      <q-card style="width: 700px; max-width: 95vw;" class="rounded-borders shadow-4">
        <q-card-section class="bg-primary text-white row items-center justify-between">
          <div class="text-h6 text-weight-bold">
            {{ editMode ? 'Editar Registro SSCO' : 'Nuevo Registro SSCO' }}
          </div>
          <q-btn flat round dense icon="close" v-close-popup />
        </q-card-section>

        <q-form @submit.prevent="guardarRegistro" class="q-pa-md">
          <div class="row q-col-gutter-sm">
            <div class="col-12 col-sm-6">
              <q-input
                v-model="form.ruc"
                label="RUC *"
                outlined
                dense
                maxlength="11"
                counter
                :rules="[
                  val => !!val || 'El RUC es obligatorio',
                  val => val.length === 11 || 'Debe contener 11 dígitos',
                  val => /^\d+$/.test(val) || 'Solo debe ingresar números'
                ]"
              />
            </div>

            <div class="col-12 col-sm-6">
              <q-input
                v-model="form.resolucion"
                label="Resolución de Atribución"
                outlined
                dense
              />
            </div>

            <div class="col-12">
              <q-input
                v-model="form.razon_social"
                label="Razón Social / Nombre *"
                outlined
                dense
                :rules="[val => !!val || 'La Razón Social es obligatoria']"
              />
            </div>

            <div class="col-12">
              <q-input
                v-model="form.domicilio_fiscal"
                label="Domicilio Fiscal"
                outlined
                dense
                type="textarea"
                rows="2"
              />
            </div>

            <div class="col-12 col-sm-4">
              <q-input
                v-model="form.fecha_emision"
                label="Fecha Emisión"
                type="date"
                outlined
                dense
                stack-label
              />
            </div>

            <div class="col-12 col-sm-4">
              <q-input
                v-model="form.fecha_firme"
                label="Fecha Firme"
                type="date"
                outlined
                dense
                stack-label
              />
            </div>

            <div class="col-12 col-sm-4">
              <q-input
                v-model="form.fecha_publicacion"
                label="Fecha Publicación"
                type="date"
                outlined
                dense
                stack-label
              />
            </div>

            <div class="col-12 col-sm-4">
              <q-input
                v-model="form.rep_documento"
                label="Doc. Representante"
                outlined
                dense
              />
            </div>

            <div class="col-12 col-sm-8">
              <q-input
                v-model="form.rep_nombre"
                label="Apellidos y Nombres del Representante"
                outlined
                dense
              />
            </div>
          </div>

          <div class="row justify-end q-mt-md q-gutter-sm">
            <q-btn flat label="Cancelar" v-close-popup />
            <q-btn color="primary" label="Guardar Registro" type="submit" :loading="guardando" unelevated />
          </div>
        </q-form>
      </q-card>
    </q-dialog>

    <!-- Modal Importación de Excel -->
    <q-dialog v-model="dialogExcel" persistent>
      <q-card style="width: 500px; max-width: 90vw;" class="rounded-borders">
        <q-card-section class="bg-teal text-white row items-center justify-between">
          <div class="text-h6 text-weight-bold">Importación Masiva desde Excel</div>
          <q-btn flat round dense icon="close" v-close-popup />
        </q-card-section>

        <q-card-section class="q-pa-md">
          <div class="text-body2 text-grey-8 q-mb-md">
            Seleccione el archivo en formato Excel (<strong>.xlsx</strong>) suministrado por SUNAT o preparado con las 9 columnas requeridas.
          </div>

          <q-file
            v-model="archivoExcel"
            outlined
            dense
            label="Seleccionar archivo .xlsx"
            accept=".xlsx"
          >
            <template v-slot:prepend>
              <q-icon name="attach_file" />
            </template>
          </q-file>
        </q-card-section>

        <q-card-actions align="right" class="q-pa-md bg-grey-1">
          <q-btn flat label="Cancelar" v-close-popup />
          <q-btn
            color="teal"
            icon="cloud_upload"
            label="Procesar Excel"
            :loading="procesandoExcel"
            :disable="!archivoExcel"
            @click="subirExcel"
            unelevated
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Modal Top 10 Usuarios -->
    <q-dialog v-model="dialogTopUsuarios">
      <q-card style="width: 800px; max-width: 95vw;" class="rounded-borders">
        <q-card-section class="bg-indigo text-white row items-center justify-between">
          <div class="text-h6 text-weight-bold">Top 10 Usuarios en Consultas API</div>
          <q-btn flat round dense icon="close" v-close-popup />
        </q-card-section>

        <q-card-section class="q-pa-none">
          <q-table
            :data="kpis.top_usuarios || []"
            :columns="columnsTop"
            row-key="user_id"
            flat
            dense
            hide-bottom
          >
            <template v-slot:body-cell-usuario="props">
              <q-td :props="props">
                <div class="text-weight-bold">
                  {{ (props.row.nombres || '') + ' ' + (props.row.apellidos || '') }}
                </div>
                <div class="text-caption text-grey-7">{{ props.row.email || ('ID Usuario: ' + props.row.user_id) }}</div>
              </q-td>
            </template>

            <template v-slot:body-cell-total_consultas="props">
              <q-td :props="props" class="text-center">
                <q-chip color="indigo-1" text-color="indigo" dense class="text-weight-bold">
                  {{ props.row.total_consultas }}
                </q-chip>
              </q-td>
            </template>

            <template v-slot:body-cell-detenciones_ssco="props">
              <q-td :props="props" class="text-center">
                <q-chip color="red-1" text-color="negative" dense class="text-weight-bold">
                  {{ props.row.detenciones_ssco || 0 }}
                </q-chip>
              </q-td>
            </template>
          </q-table>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script>
import axios from "axios";

export default {
  name: "SSCOIndex",
  filters: {
    formatNumber(val) {
      if (!val && val !== 0) return "0";
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
  },
  data() {
    return {
      busqueda: "",
      loading: false,
      cargandoSunat: false,
      procesandoExcel: false,
      guardando: false,
      dialogForm: false,
      dialogExcel: false,
      dialogTopUsuarios: false,
      editMode: false,
      archivoExcel: null,
      listaSSCO: [],
      kpis: {
        total_ssco: 0,
        total_consultas: 0,
        detecciones_positivas: 0,
        top_usuarios: [],
        tendencia_30_dias: []
      },
      pagination: {
        page: 1,
        rowsPerPage: 10,
        rowsNumber: 0
      },
      form: {
        id: null,
        ruc: "",
        razon_social: "",
        domicilio_fiscal: "",
        resolucion: "",
        fecha_emision: "",
        fecha_firme: "",
        rep_documento: "",
        rep_nombre: "",
        fecha_publicacion: ""
      },
      columns: [
        { name: "ruc", label: "RUC", field: "ruc", align: "left", sortable: true },
        { name: "razon_social", label: "Razón Social / Domicilio", field: "razon_social", align: "left", sortable: true },
        { name: "resolucion", label: "Resolución", field: "resolucion", align: "left" },
        { name: "fechas", label: "Fechas Clave", field: "fechas", align: "left" },
        { name: "representante", label: "Representante Legal", field: "rep_nombre", align: "left" },
        { name: "actions", label: "Acciones", field: "actions", align: "right" }
      ],
      columnsTop: [
        { name: "usuario", label: "Usuario", field: "usuario", align: "left" },
        { name: "total_consultas", label: "Consultas API", field: "total_consultas", align: "center" },
        { name: "detenciones_ssco", label: "Coincidencias SSCO", field: "detenciones_ssco", align: "center" },
        { name: "ultima_consulta", label: "Última Consulta", field: "ultima_consulta", align: "center" }
      ]
    };
  },
  created() {
    this.cargarEstadisticas();
    this.cargarDatos();
  },
  methods: {
    async cargarEstadisticas() {
      try {
        const res = await axios.get("ApiWeb/ssco.php?action=estadisticas");
        if (res.data && res.data.success) {
          this.kpis = res.data.data;
        }
      } catch (err) {
        console.error("Error al cargar estadísticas SSCO:", err);
      }
    },
    async cargarDatos() {
      this.loading = true;
      try {
        const params = {
          action: "listar",
          busqueda: this.busqueda,
          limite: this.pagination.rowsPerPage,
          pagina: this.pagination.page
        };
        const res = await axios.get("ApiWeb/ssco.php", { params });
        if (res.data && res.data.success) {
          this.listaSSCO = res.data.data || [];
          this.pagination.rowsNumber = res.data.total || 0;
        }
      } catch (err) {
        this.$q.notify({
          type: "negative",
          message: "Error al obtener registros del padrón SSCO."
        });
      } finally {
        this.loading = false;
      }
    },
    onRequest(props) {
      const { page, rowsPerPage } = props.pagination;
      this.pagination.page = page;
      this.pagination.rowsPerPage = rowsPerPage;
      this.cargarDatos();
    },
    buscar() {
      this.pagination.page = 1;
      this.cargarDatos();
    },
    async sincronizarSunat() {
      this.cargandoSunat = true;
      try {
        const res = await axios.get("ApiWeb/ssco.php?action=sincronizar_sunat");
        if (res.data && res.data.success) {
          this.$q.notify({
            type: "positive",
            message: res.data.message || "Padrón SSCO sincronizado exitosamente desde SUNAT."
          });
          this.cargarEstadisticas();
          this.cargarDatos();
        } else {
          this.notificarFallaSunat(res.data ? res.data.message : null);
        }
      } catch (err) {
        this.notificarFallaSunat();
      } finally {
        this.cargandoSunat = false;
      }
    },
    notificarFallaSunat(msj) {
      this.$q.notify({
        type: "negative",
        color: "negative",
        icon: "warning",
        timeout: 6000,
        message: msj || "No se pudo actualizar desde la web de SUNAT. Por favor descargue el Excel e impórtelo manualmente."
      });
    },
    async subirExcel() {
      if (!this.archivoExcel) return;
      this.procesandoExcel = true;
      try {
        const formData = new FormData();
        formData.append("file", this.archivoExcel);
        formData.append("action", "importar_excel");

        const res = await axios.post("ApiWeb/ssco.php?action=importar_excel", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });

        if (res.data && res.data.success) {
          this.$q.notify({
            type: "positive",
            message: res.data.message || "Importación masiva completada con éxito."
          });
          this.dialogExcel = false;
          this.archivoExcel = null;
          this.cargarEstadisticas();
          this.cargarDatos();
        } else {
          this.$q.notify({
            type: "negative",
            message: (res.data && res.data.message) ? res.data.message : "Error al procesar el archivo Excel."
          });
        }
      } catch (err) {
        this.$q.notify({
          type: "negative",
          message: "No se pudo subir o procesar el archivo Excel."
        });
      } finally {
        this.procesandoExcel = false;
      }
    },
    abrirCrear() {
      this.editMode = false;
      this.form = {
        id: null,
        ruc: "",
        razon_social: "",
        domicilio_fiscal: "",
        resolucion: "",
        fecha_emision: "",
        fecha_firme: "",
        rep_documento: "",
        rep_nombre: "",
        fecha_publicacion: ""
      };
      this.dialogForm = true;
    },
    abrirEditar(row) {
      this.editMode = true;
      this.form = { ...row };
      this.dialogForm = true;
    },
    async guardarRegistro() {
      this.guardando = true;
      try {
        const action = this.editMode ? "actualizar" : "guardar";
        const payload = { ...this.form, action };
        const res = await axios.post(`ApiWeb/ssco.php?action=${action}`, payload);

        if (res.data && res.data.success) {
          this.$q.notify({
            type: "positive",
            message: res.data.message || "Registro guardado correctamente."
          });
          this.dialogForm = false;
          this.cargarEstadisticas();
          this.cargarDatos();
        } else {
          this.$q.notify({
            type: "negative",
            message: (res.data && res.data.message) ? res.data.message : "Error al guardar el registro."
          });
        }
      } catch (err) {
        this.$q.notify({
          type: "negative",
          message: "Ocurrió un error al intentar guardar el registro."
        });
      } finally {
        this.guardando = false;
      }
    },
    confirmarEliminar(row) {
      this.$q.dialog({
        title: "Confirmar eliminación",
        message: `¿Está seguro de eliminar el registro RUC ${row.ruc} - ${row.razon_social}?`,
        cancel: true,
        persistent: true
      }).onOk(() => {
        this.eliminarRegistro(row.id);
      });
    },
    async eliminarRegistro(id) {
      try {
        const res = await axios.post("ApiWeb/ssco.php?action=eliminar", { id, action: "eliminar" });
        if (res.data && res.data.success) {
          this.$q.notify({
            type: "positive",
            message: res.data.message || "Registro eliminado del padrón."
          });
          this.cargarEstadisticas();
          this.cargarDatos();
        }
      } catch (err) {
        this.$q.notify({
          type: "negative",
          message: "No se pudo eliminar el registro."
        });
      }
    },
    abrirModalTopUsuarios() {
      this.cargarEstadisticas();
      this.dialogTopUsuarios = true;
    }
  }
};
</script>

<style scoped>
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
